import update from 'immutability-helper'

import {
  CANCEL_GET_AI_MESSAGE,
  CANCEL_ALL_CONVERSATIONS,
  CLEAR_GET_AI_CONVERSATIONS,
  CLEAR_GET_CHANGE_HISTORY,
  CLEAR_GET_SPONSOR_FORM_INDUSTRY_SUGGESTIONS,
  CLEAR_SELECTORS,
  CLEAR_UPDATE_AI_CONVERSATION,
  CLEAR_UPDATE_SELECTOR,
  GET_AI_CONVERSATION_MESSAGES,
  GET_AI_CONVERSATION_MESSAGES_FAILURE,
  GET_AI_CONVERSATION_MESSAGES_SUCCESS,
  GET_AI_CONVERSATIONS,
  GET_AI_CONVERSATIONS_FAILURE,
  GET_AI_CONVERSATIONS_SUCCESS,
  GET_AI_MESSAGE,
  GET_AI_MESSAGE_CHUNK,
  GET_AI_MESSAGE_FAILURE,
  GET_AI_MESSAGE_SUCCESS,
  GET_CHANGE_HISTORY,
  GET_CHANGE_HISTORY_FAILURE,
  GET_CHANGE_HISTORY_SUCCESS,
  GET_SELECTORS,
  GET_SELECTORS_FAILURE,
  GET_SELECTORS_SUCCESS,
  GET_SPONSOR_FORM_INDUSTRY_SUGGESTIONS,
  GET_SPONSOR_FORM_INDUSTRY_SUGGESTIONS_CHUNK,
  GET_SPONSOR_FORM_INDUSTRY_SUGGESTIONS_FAILURE,
  GET_SPONSOR_FORM_INDUSTRY_SUGGESTIONS_SUCCESS,
  SET_IS_NEW_CHAT_MODE,
  SET_SELECTED_AI_CONVERSATION_ID,
  UPDATE_AI_CONVERSATION,
  UPDATE_AI_CONVERSATION_FAILURE,
  UPDATE_AI_CONVERSATION_SUCCESS,
  UPDATE_SELECTOR,
  UPDATE_SELECTOR_FAILURE,
  UPDATE_SELECTOR_SUCCESS
} from '../actions/tools'
import {
  INITIAL_GET_RESULTS_PAGING_REDUCER,
  INITIAL_GET_RESULTS_REDUCER,
  INITIAL_UPDATE_REDUCER
} from '../../constants/reducer'
import { handleAddUserMessageToConversation } from '../../helpers/modules/tools'
import { AI_CHAT, getNewChatObject, TEMP_ID_NEW_CONVERSATION } from '../../pages/ChatPage/fields'

const initialState = {
  // PLOP_APPEND_PATTERN_ANCHOR_INITIAL_REDUCER
  changeHistory: INITIAL_GET_RESULTS_PAGING_REDUCER,
  selectors: INITIAL_GET_RESULTS_REDUCER,
  updatedSelector: INITIAL_UPDATE_REDUCER,
  [AI_CHAT]: {
    conversationId: null,
    isNewChatMode: false
  },
  aiConversations: {
    ...INITIAL_GET_RESULTS_PAGING_REDUCER,
    byId: {
      [TEMP_ID_NEW_CONVERSATION]: getNewChatObject()
    },
    allIds: []
  },
  updateAiConversation: {
    ...INITIAL_UPDATE_REDUCER,
    ids: {}
  },
  sponsorFormIndustrySuggestions: INITIAL_GET_RESULTS_REDUCER
}

export default function tools(state = initialState, action) {
  switch (action.type) {
    // PLOP_APPEND_PATTERN_ANCHOR_REDUCERS

    // Get ai suggestions
    case GET_AI_MESSAGE:
      const isExistingConversation = action.params?.params?.extra_context?.conversation_id

      return update(state, {
        [AI_CHAT]: {
          ...(!isExistingConversation && {
            conversationId: {
              $set: TEMP_ID_NEW_CONVERSATION
            },
            isNewChatMode: {
              // when a new user message is added to the new conversation, set isNewChatMode false to see the chat
              $set: false
            }
          })
        },

        // Handle adding a user message to the conversation
        ...handleAddUserMessageToConversation({ state, action, isExistingConversation })
      })

    case GET_AI_MESSAGE_CHUNK:
      // Find the conversation with the matching request_id
      const requestId = action.dataChunk.requestId

      // Find the conversation ID that matches the request_id
      const matchingConversationId = Object.keys(state.aiConversations.byId).find(
        id => state.aiConversations.byId[id].request_id === requestId
      )

      const currentConversation = state.aiConversations.byId[matchingConversationId]

      if (!currentConversation) {
        // If no conversation is found, return the state
        return state
      }

      const wasNewAIMessageAddedToConversation =
        currentConversation?.messages?.results?.[currentConversation.messages.results.length - 1]?.role === 'assistant'

      // If the conversation ID is changing (from TEMP_ID_NEW_CONVERSATION to a real ID)
      const isConversationIdChanging =
        matchingConversationId === TEMP_ID_NEW_CONVERSATION && action.dataChunk.conversationId

      const newConversationId = isConversationIdChanging ? action.dataChunk.conversationId : matchingConversationId

      // if the last message was from the assistant, we need to replace it with the new one we received
      const updatedCurrentConversation = {
        ...currentConversation,
        id: newConversationId,
        // Ensure request_id is preserved
        request_id: requestId,
        messages: {
          ...currentConversation.messages,
          results: wasNewAIMessageAddedToConversation
            ? [
                ...currentConversation.messages.results.slice(0, currentConversation.messages.results.length - 1),
                {
                  role: 'assistant',
                  content: action.dataChunk.text
                }
              ]
            : [
                ...currentConversation.messages.results,
                {
                  role: 'assistant',
                  content: action.dataChunk.text
                }
              ]
        }
      }

      // Create update object for the state
      const updateObject = {
        [AI_CHAT]: {
          ...(isConversationIdChanging && {
            conversationId: {
              $set: action.dataChunk.conversationId
            }
          })
        },
        aiConversations: {
          byId: {}
        }
      }

      // If the conversation ID is changing, we need to:
      // 1. Remove the temporary conversation
      // 2. Add the new conversation with the real ID
      // 3. Update the allIds array to replace the temp ID with the real ID
      if (isConversationIdChanging) {
        updateObject.aiConversations.byId.$unset = [TEMP_ID_NEW_CONVERSATION]
        updateObject.aiConversations.byId.$merge = {
          [newConversationId]: updatedCurrentConversation
        }
        updateObject.aiConversations.allIds = {
          $apply: ids => ids.map(id => (id === TEMP_ID_NEW_CONVERSATION ? newConversationId : id))
        }
      } else {
        // Just update the existing conversation
        updateObject.aiConversations.byId.$merge = {
          [matchingConversationId]: updatedCurrentConversation
        }
      }

      return update(state, updateObject)

    case GET_AI_MESSAGE_SUCCESS:
      // Find the conversation with the matching request_id
      const successRequestId = action?.requestId
      const successConversationId = Object.keys(state.aiConversations.byId).find(
        id => state.aiConversations.byId[id].request_id === successRequestId
      )

      return update(state, {
        ...(successConversationId && {
          aiConversations: {
            byId: {
              [successConversationId]: {
                $merge: {
                  isLoading: false
                }
              }
            }
          }
        })
      })

    case GET_AI_MESSAGE_FAILURE:
      // Find the conversation with the matching request_id
      const failureRequestId = action?.requestId
      const failureConversationId = Object.keys(state.aiConversations.byId).find(
        id => state.aiConversations.byId[id].request_id === failureRequestId
      )

      return update(state, {
        ...(failureConversationId && {
          aiConversations: {
            byId: {
              [failureConversationId]: {
                $merge: {
                  isLoading: false
                }
              }
            }
          }
        })
      })

    case CANCEL_GET_AI_MESSAGE:
      // Only stop the specified conversation
      const stopConversationId = action.conversationId
      const stopRequestId = action.requestId

      // Only proceed if we have a valid conversation ID
      if (!stopConversationId || !state.aiConversations.byId[stopConversationId]) {
        return state
      }

      // Check if the requestId matches the conversation's request_id
      if (stopRequestId && state.aiConversations.byId[stopConversationId].request_id !== stopRequestId) {
        return state
      }

      return update(state, {
        aiConversations: {
          byId: {
            [stopConversationId]: {
              $merge: {
                isLoading: false
              }
            }
          }
        }
      })

    case CANCEL_ALL_CONVERSATIONS:
      // Find all conversations that are currently loading
      const loadingConversationIds = Object.keys(state.aiConversations.byId).filter(
        id => state.aiConversations.byId[id].isLoading
      )

      // If no conversations are loading, return the state unchanged
      if (loadingConversationIds.length === 0) {
        return state
      }

      // Create an update object that sets isLoading to false for all loading conversations
      const updateObj = {
        aiConversations: {
          byId: {}
        }
      }

      // Add each loading conversation to the update object
      loadingConversationIds.forEach(id => {
        updateObj.aiConversations.byId[id] = {
          $merge: {
            isLoading: false
          }
        }
      })

      return update(state, updateObj)

    // set conversationId
    case SET_SELECTED_AI_CONVERSATION_ID:
      return update(state, {
        [AI_CHAT]: {
          $merge: {
            conversationId: action.conversationId,
            // if we are in new chat mode, and user select existing conversation, set isNewChatMode to false
            ...(state[AI_CHAT].isNewChatMode &&
              action.conversationId !== TEMP_ID_NEW_CONVERSATION && {
                isNewChatMode: false
              })
          }
        }
      })

    // set is new chat mode
    case SET_IS_NEW_CHAT_MODE:
      return update(state, {
        [AI_CHAT]: {
          $merge: {
            isNewChatMode: action.isNewChatMode
          }
        }
      })

    // Get ai conversations
    case GET_AI_CONVERSATIONS:
      return update(state, {
        aiConversations: {
          $merge: {
            ...(action.loadOptions.shouldClearExistingState && INITIAL_GET_RESULTS_PAGING_REDUCER),
            isLoading: true
          }
        }
      })
    case GET_AI_CONVERSATIONS_SUCCESS:
      const newConversations = action.data.results.reduce((acc, conversation) => {
        acc[conversation.id] = {
          ...conversation,
          // we fetch messages using separate endpoint, here we add messages field as placeholder
          messages: INITIAL_GET_RESULTS_PAGING_REDUCER
        }
        return acc
      }, {})

      const newConversationIds = action.data.results.map(conversation => conversation.id)

      return update(state, {
        aiConversations: {
          $merge: {
            isLoading: false,
            wasLoaded: true,
            paging: {
              next: action.data.next
            }
          },
          byId: {
            $merge: newConversations
          },
          allIds: {
            $push: newConversationIds
          }
        }
      })
    case GET_AI_CONVERSATIONS_FAILURE:
      return update(state, {
        aiConversations: {
          $merge: {
            isLoading: false,
            error: action.error
          }
        }
      })
    case CLEAR_GET_AI_CONVERSATIONS:
      return update(state, {
        aiConversations: { $set: initialState.aiConversations }
      })

    // Get ai conversation messages
    case GET_AI_CONVERSATION_MESSAGES:
      const getMessagesConversationId = action.params.id

      return update(state, {
        aiConversations: {
          byId: {
            [getMessagesConversationId]: {
              messages: {
                $merge: {
                  ...(action.loadOptions.shouldClearExistingState && INITIAL_GET_RESULTS_PAGING_REDUCER),
                  isLoading: true,
                  error: null
                }
              }
            }
          }
        }
      })
    case GET_AI_CONVERSATION_MESSAGES_SUCCESS:
      const messagesConversationId = action.conversationId

      return update(state, {
        aiConversations: {
          byId: {
            [messagesConversationId]: {
              messages: {
                $merge: {
                  isLoading: false,
                  wasLoaded: true,
                  paging: {
                    next: action.data.next
                  }
                },
                results: {
                  $unshift: [...action.data.results.messages].reverse()
                }
              }
            }
          }
        }
      })
    case GET_AI_CONVERSATION_MESSAGES_FAILURE:
      const messagesConversationIdFailure = action.conversationId

      return update(state, {
        aiConversations: {
          byId: {
            [messagesConversationIdFailure]: {
              messages: {
                $merge: {
                  isLoading: false,
                  error: action.error
                }
              }
            }
          }
        }
      })

    // Get sponsor form industry suggestions
    case GET_SPONSOR_FORM_INDUSTRY_SUGGESTIONS:
      return update(state, {
        sponsorFormIndustrySuggestions: { $merge: { isLoading: true } }
      })
    case GET_SPONSOR_FORM_INDUSTRY_SUGGESTIONS_CHUNK:
      return update(state, {
        sponsorFormIndustrySuggestions: {
          $merge: {
            results: action.dataChunk
          }
        }
      })
    case GET_SPONSOR_FORM_INDUSTRY_SUGGESTIONS_SUCCESS:
      return update(state, {
        sponsorFormIndustrySuggestions: {
          $merge: {
            isLoading: false,
            wasLoaded: true
          }
        }
      })
    case GET_SPONSOR_FORM_INDUSTRY_SUGGESTIONS_FAILURE:
      return update(state, {
        sponsorFormIndustrySuggestions: {
          $merge: {
            isLoading: false,
            error: action.error
          }
        }
      })
    case CLEAR_GET_SPONSOR_FORM_INDUSTRY_SUGGESTIONS:
      return update(state, {
        sponsorFormIndustrySuggestions: { $set: initialState.sponsorFormIndustrySuggestions }
      })

    // Get updates history
    case GET_CHANGE_HISTORY:
      return update(state, {
        changeHistory: { $merge: { isLoading: true } }
      })
    case GET_CHANGE_HISTORY_SUCCESS:
      return update(state, {
        changeHistory: {
          $merge: {
            isLoading: false,
            wasLoaded: true,
            results: action.data.data,
            paging: {
              next: action.data.next
            }
          }
        }
      })
    case GET_CHANGE_HISTORY_FAILURE:
      return update(state, {
        changeHistory: {
          $merge: {
            isLoading: false,
            error: action.error
          }
        }
      })
    case CLEAR_GET_CHANGE_HISTORY:
      return update(state, {
        changeHistory: { $set: initialState.changeHistory }
      })
    case GET_SELECTORS:
      return update(state, {
        selectors: { $merge: { isLoading: true } }
      })
    case GET_SELECTORS_SUCCESS:
      return update(state, {
        selectors: {
          $merge: {
            isLoading: false,
            wasLoaded: true,
            results: action.selectorsData
          }
        }
      })
    case GET_SELECTORS_FAILURE:
      return update(state, {
        selectors: {
          $merge: {
            isLoading: false,
            error: action.error
          }
        }
      })
    case CLEAR_SELECTORS:
      return update(state, { selectors: { $set: initialState.selectors } })
    case UPDATE_SELECTOR:
      return update(state, {
        updatedSelector: { $merge: { isLoading: true } }
      })
    case UPDATE_SELECTOR_SUCCESS:
      return update(state, {
        selectors: {
          $merge: {
            results: action.selectorData
          }
        },
        updatedSelector: {
          $merge: {
            isLoading: false,
            wasUpdated: true,
            data: action.selectorData
          }
        }
      })
    case UPDATE_SELECTOR_FAILURE:
      return update(state, {
        updatedSelector: {
          $merge: {
            isLoading: false,
            error: action.error
          }
        }
      })
    case CLEAR_UPDATE_SELECTOR:
      return update(state, { updatedSelector: { $set: initialState.updatedSelector } })

    // Update ai conversation
    case UPDATE_AI_CONVERSATION:
      return update(state, {
        updateAiConversation: {
          ids: {
            $merge: {
              [action.id]: true
            }
          }
        }
      })
    case UPDATE_AI_CONVERSATION_SUCCESS:
      return update(state, {
        updateAiConversation: {
          ids: {
            $unset: [action.id]
          },
          $merge: {
            wasUpdated: true,
            data: action.data
          }
        },
        aiConversations: {
          byId: {
            [action.id]: {
              $merge: action.data
            }
          }
        }
      })
    case UPDATE_AI_CONVERSATION_FAILURE:
      return update(state, {
        updateAiConversation: {
          ids: {
            $unset: [action.id]
          },
          $merge: {
            error: action.error
          }
        }
      })
    case CLEAR_UPDATE_AI_CONVERSATION:
      return update(state, {
        updateAiConversation: { $set: initialState.updateAiConversation }
      })
    default:
      return state
  }
}
